import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Flex,
  Input,
  Popover,
  Select,
  SelectProps,
  Spin,
  Tag,
  Tooltip
} from "antd";
import { useTranslation } from "react-i18next";

import { Product } from "@/types/product";
import { categoriesFetch, productFetchs } from "@/apis/product";
import { debounce } from "lodash";
import SText from "@/styles/SText";
import { PAGE_SIZE } from "@/utils/const";

interface SProductSelectProps extends SelectProps {
  value?: string;
  onChange?: (value: string) => void;
  defaultList?: Array<Product>;
  width?: string;
  getSelectPro?: (data: Product | null) => void;
  disabledProducts?: string[];
  onlyPreReady?: boolean;
  onlyOrg?: boolean;
}

const ProductSelect = ({
  value,
  onChange,
  defaultList,
  width,
  getSelectPro,
  disabledProducts = [],
  onlyPreReady = false,
  onlyOrg = false,
  ...props
}: SProductSelectProps) => {
  const { t } = useTranslation();

  const [list, setList] = useState<Array<Product>>(
    defaultList ? defaultList : []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(1);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [rootCategories, setRootCategories] = useState<string[] | undefined>(undefined);
  const fetchingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (onlyOrg) {
      setListLoading(true);
      categoriesFetch()
        .then(res => {
          setRootCategories(res.body?.map(cat => cat.id))
        })
        .catch(() => setRootCategories(undefined))
        .finally(() => setListLoading(false))
    }
    return () => abortControllerRef.current?.abort();
  }, [onlyOrg]);

  const fetch = useCallback((keyword: string, current: number) => {
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();

    // 避免在已有请求时设置 loading，减少闪烁
    if (!fetchingRef.current) {
      setListLoading(true);
    }
    fetchingRef.current = true;

    productFetchs({
      body: {
        keyword,
        preReady: onlyPreReady ? 1 : undefined,
        rootCategories
      },
      pagination: { offset: (current - 1) * PAGE_SIZE, take: PAGE_SIZE }
    })
      .then(res => {
        if (current === 1) {
          const info = defaultList ? defaultList : [];
          const restList = res.body.filter(
            item => !info.map(dI => dI.id)?.includes(item.id)
          );
          setList([...info, ...restList]);
        } else {
          setList(prevList => [...prevList, ...res.body]);
        }
        setTotal(res.pagination?.size ?? 0);
      })
      .catch(err => {
        // 忽略取消请求的错误
        if (err.name !== 'AbortError') {
          console.error('Fetch products error:', err);
        }
      })
      .finally(() => {
        fetchingRef.current = false;
        setListLoading(false);
      });
  }, [defaultList, onlyOrg, onlyPreReady, rootCategories]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (
      !fetchingRef.current &&
      currentPage * PAGE_SIZE < total &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight
    ) {
      let page = currentPage + 1;
      fetch(keyword, page);
      setCurrentPage(page);
    }
  };

  const onSearch = useRef(
    debounce((val: string) => {
      setKeyword(val.trim());
      setCurrentPage(1);
      fetch(val.trim(), 1);
    }, 300)
  ).current;

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      onSearch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [onSearch]);

  const getContent: (item: Product) => ReactNode = item => {
    return (
      <div>
        <div>
          {t("product.code")}: {item.code}
        </div>
        <div>
          {t("product.name")}: {item.name}
        </div>
        <div>
          {t("product.brand1")}: {item.model?.toLocaleUpperCase()}
        </div>
        <div>
          {t("product.brand2")}: {item.t1?.toLocaleUpperCase()}
        </div>
        <div>
          {t("product.uom")}: {item?.uom?.toLocaleUpperCase()}
        </div>
        <div>
          {t("product.cbm")}: {item.cbm}
        </div>
      </div>
    );
  };

  return (
    <Tooltip title={value ? "" : t("components.plProduct")}>
      <Select
        value={value}
        filterOption={false}
        onDropdownVisibleChange={open => {
          if (open) {
            setKeyword("");
            setSearchInput("");
            setCurrentPage(1);
            fetch("", 1);
          }
        }}
        onSelect={val => {
          getSelectPro && getSelectPro(list.find(i => i.id === val) || null);
          onChange && onChange(val);
        }}
        style={{ width: width ? width : "100%" }}
        placeholder={t("components.plProduct")}
        options={list.filter(item => !disabledProducts.includes(item.id)).map(item => ({
          key: item.id,
          value: item.id,
          label: (
            <Popover showArrow={false} content={() => getContent(item)}>
              <Flex gap={8} align="center" style={{ width: "100%" }}>
                <Tag
                  color="green"
                  style={{
                    width: "110px",
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {item.code}
                </Tag>
                <SText
                  style={{
                    width: "200px",
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                  text={item.name}
                />
                <SText
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                  text={item.model?.toLocaleUpperCase() || "-"}
                />
              </Flex>
            </Popover>
          )
        }))}
        onPopupScroll={handleScroll}
        dropdownRender={ele => (
          <>
            <div style={{ padding: "8px" }}>
              <Input.Search
                placeholder="搜索"
                value={searchInput}
                onChange={e => {
                  setSearchInput(e.target.value);
                  onSearch(e.target.value);
                }}
                onSearch={val => {
                  onSearch(val);
                }}
                style={{ width: "100%" }}
              />
            </div>
            {ele}
            {listLoading && (
              <Flex
                style={{ width: "100%", padding: 10 }}
                justify="center"
                align="center"
              >
                <Spin />
              </Flex>
            )}
          </>
        )}
        {...props}
      />
    </Tooltip>
  );
};

export default ProductSelect;
